import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { PaymentsPayoutService } from "@/modules/dashboard/dispersions/services/paymentsPayout.service";
import { PaymentsPayinService } from "@/modules/dashboard/payins/services/paymentsPayin.service";
import { SwapService } from "@/modules/dashboard/swaps/services/swap.service";
import { HttpApiService } from "@/shared/services/httpApi.service";

import {
  LEDGER_POLL_INTERVAL_MS,
  LEDGER_REVERSAL_TYPE,
  LEDGER_TYPE_BY_KIND,
} from "../constants/paymentsLedger.constants";
import { PaymentsLedgerService } from "../services/paymentsLedger.service";
import type {
  LedgerLeg,
  LedgerOperationCandidate,
  LedgerOperationKind,
  LedgerOperationView,
} from "../types/paymentsLedger.types";
import { ledgerAccessLatch } from "../utils/ledgerAccessLatch";
import { nativeSwapToCandidate, payinToCandidate, payoutToCandidate } from "../utils/ledgerCandidates";
import { buildLedgerOperationView, mergeLedgerLegs } from "../utils/ledgerGrouping";

export interface UseLedgerOperationParams {
  kind: LedgerOperationKind | null;
  operationId: string | null;
  /**
   * The row the explorer already has for this operation, when it came from there. It provides the
   * organization immediately, which is what lets a swap's entity be read at all.
   */
  pickerCandidate: LedgerOperationCandidate | null;
}

export interface UseLedgerOperationResult {
  /** Grouped journal, or null while nothing is selected / the read failed. */
  view: LedgerOperationView | null;
  /** The operation as the runtime reports it — the entity read, falling back to the explorer row. */
  candidate: LedgerOperationCandidate | null;
  /** First load of this operation's journal — the pane shows the skeleton. Refreshes never do. */
  isLoading: boolean;
  isRefreshing: boolean;
  /** Non-access failure reading the journal — the caller renders an error, NEVER an empty ledger as truth. */
  hasError: boolean;
  /** False after a 401/403 — the caller renders the no-access view. */
  hasAccess: boolean;
  /** The entity could not be read, so the header has no live status. The journal is still real. */
  isEntityUnavailable: boolean;
  lastUpdatedAt: Date | null;
  refresh: () => void;
}

interface LedgerSnapshot {
  key: string;
  legs: LedgerLeg[];
  failed: boolean;
  at: Date;
}

interface EntitySnapshot {
  key: string;
  candidate: LedgerOperationCandidate | null;
  failed: boolean;
}

/**
 * Everything about the operation currently open: its journal entries and its live runtime state.
 *
 * The two reads live in one hook because they depend on each other in one direction. The journal needs only
 * the id, and it is what reveals the organization (through the org-scoped client-liability account) — which
 * the swap detail endpoint requires and a deep link does not carry. Resolving them separately would mean
 * synchronizing state between two hooks; here the organization is simply derived.
 *
 * Two requests are always issued for the journal: the operation's own `operation_type` and `REVERSE`.
 * `LedgerService.reverse` stamps the mirror entry with `REVERSE` and the original `operation_id`, and the
 * endpoint filters strictly by type — so asking only for `PAYOUT` shows a payout that looks like it was never
 * reversed. They are merged by `seq`, the only strict chronological order.
 *
 * Freshness: a terminal operation is immutable and read once. While it can still move (a settle appends an
 * entry) both reads repeat every 15 s — the payments-activity cadence. There is no websocket for payments.
 *
 * An unknown id is not a 404 here: the ledger answers `200 []`, and so does a real operation that has posted
 * nothing yet. Both are "no entries", never an error.
 */
export const useLedgerOperation = ({
  kind,
  operationId,
  pickerCandidate,
}: UseLedgerOperationParams): UseLedgerOperationResult => {
  const { user } = useAuth();
  const deniedKey = user?.id ?? "anon";

  const [ledgerSnapshot, setLedgerSnapshot] = useState<LedgerSnapshot | null>(null);
  const [entitySnapshot, setEntitySnapshot] = useState<EntitySnapshot | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Read through the latch, never copied into state: the copy would keep this pane loading forever if the
  // explorer latched first, and would freeze whatever `deniedKey` the first render happened to see.
  const hasAccess = !ledgerAccessLatch.useDenied(deniedKey);
  const [reloadKey, setReloadKey] = useState(0);

  // The latest issued request wins — a stale slow response must never overwrite a newer one.
  const ledgerSeq = useRef(0);
  const entitySeq = useRef(0);

  const key = kind && operationId ? `${kind}|${operationId}` : null;

  // Derived, not synchronized: a snapshot from another operation simply stops matching the key.
  const ledgerCurrent = ledgerSnapshot?.key === key ? ledgerSnapshot : null;
  const entityCurrent = entitySnapshot?.key === key ? entitySnapshot : null;

  const view = useMemo(
    () => (ledgerCurrent && !ledgerCurrent.failed ? buildLedgerOperationView(ledgerCurrent.legs) : null),
    [ledgerCurrent],
  );

  const candidate = entityCurrent?.candidate ?? pickerCandidate;

  /** The explorer row first, then the journal's own account scope — the only source on a deep link. */
  const organizationIdHint = pickerCandidate?.organizationId ?? view?.organizationIds[0] ?? null;
  // A swap's detail endpoint resolves tenancy through `requireOrg` and 400s without an organization.
  const canFetchEntity = key !== null && (kind !== "swaps" || organizationIdHint !== null);

  const fetchLedger = useCallback(
    async (options: { background: boolean }) => {
      if (!kind || !operationId || ledgerAccessLatch.isDenied(deniedKey)) return;
      const snapshotKey = `${kind}|${operationId}`;
      const ledgerType = LEDGER_TYPE_BY_KIND[kind];
      const seq = ++ledgerSeq.current;

      try {
        const responses = await Promise.all([
          PaymentsLedgerService.listByOperation(ledgerType, operationId),
          PaymentsLedgerService.listByOperation(LEDGER_REVERSAL_TYPE, operationId),
        ]);
        if (seq !== ledgerSeq.current) return;
        setLedgerSnapshot({
          key: snapshotKey,
          legs: mergeLedgerLegs(...responses.map((response) => response.data)),
          failed: false,
          at: new Date(),
        });
      } catch (err: unknown) {
        if (seq !== ledgerSeq.current) return;
        if (HttpApiService.isAccessDeniedError(err)) {
          ledgerAccessLatch.latch(deniedKey);
          return;
        }
        // Background failures keep the last known-good journal; a failed first read shows the error state.
        if (!options.background) {
          setLedgerSnapshot({ key: snapshotKey, legs: [], failed: true, at: new Date() });
        }
      } finally {
        if (seq === ledgerSeq.current) setIsRefreshing(false);
      }
    },
    [deniedKey, kind, operationId],
  );

  const fetchEntity = useCallback(async () => {
    if (!kind || !operationId || !canFetchEntity) return;
    const snapshotKey = `${kind}|${operationId}`;
    const seq = ++entitySeq.current;

    try {
      const next =
        kind === "payouts"
          ? payoutToCandidate((await PaymentsPayoutService.getPayout(operationId)).data)
          : kind === "payins"
            ? payinToCandidate((await PaymentsPayinService.getPayin(operationId)).data)
            : nativeSwapToCandidate(
                (await SwapService.getSwap(operationId, organizationIdHint)).data.swap,
                organizationIdHint ?? "",
              );
      if (seq !== entitySeq.current) return;
      setEntitySnapshot({ key: snapshotKey, candidate: next, failed: false });
    } catch (err: unknown) {
      if (seq !== entitySeq.current) return;
      if (HttpApiService.isAccessDeniedError(err)) {
        ledgerAccessLatch.latch(deniedKey);
        return;
      }
      // Best-effort: the header degrades to the journal's own data, the trail is unaffected.
      setEntitySnapshot({ key: snapshotKey, candidate: null, failed: true });
    }
  }, [canFetchEntity, deniedKey, kind, operationId, organizationIdHint]);

  useEffect(() => {
    void fetchLedger({ background: false });
  }, [fetchLedger, reloadKey]);

  useEffect(() => {
    void fetchEntity();
  }, [fetchEntity, reloadKey]);

  // Only an operation that can still move is re-read.
  const isNonTerminal = candidate?.isNonTerminal ?? false;
  useEffect(() => {
    if (!isNonTerminal || !hasAccess || key === null) return;
    const interval = setInterval(() => {
      setIsRefreshing(true);
      void fetchLedger({ background: true });
      void fetchEntity();
    }, LEDGER_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isNonTerminal, hasAccess, key, fetchEntity, fetchLedger]);

  const refresh = useCallback(() => {
    // With nothing selected the fetchers bail out before their `finally`, which would leave the spinner
    // running forever.
    if (key !== null) setIsRefreshing(true);
    setReloadKey((previous) => previous + 1);
  }, [key]);

  return {
    view,
    candidate,
    isLoading: key !== null && hasAccess && ledgerCurrent === null,
    isRefreshing,
    hasError: ledgerCurrent?.failed ?? false,
    hasAccess,
    // Silent when the explorer already gave us the row — nothing is actually missing then.
    isEntityUnavailable: (entityCurrent?.failed ?? false) && pickerCandidate === null,
    lastUpdatedAt: ledgerCurrent && !ledgerCurrent.failed ? ledgerCurrent.at : null,
    refresh,
  };
};
